import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Heart,
  Eye,
  ExternalLink,
  Github,
  AlertCircle,
  Loader2,
  Scale,
} from "lucide-react";
import { env } from "@/config/env";
import { logger } from "@/lib/logger";
import { useAgent } from "@/hooks/useAgents";
import { useLikeTracking } from "@/hooks/useLikeTracking";
import { formatNumber } from "@/lib/data-transform";
import MarkdownRenderer from "@/components/MarkdownRenderer";

const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { agent, loading, error, likeAgent } = useAgent(id || "");
  const { canLike, recordLike } = useLikeTracking();
  const [isLiking, setIsLiking] = useState(false);
  const [likeStatus, setLikeStatus] = useState<
    "idle" | "success" | "error" | "already-liked"
  >("idle");

  // Debug logging
  logger.debug("AgentDetail", {
    id,
    agent,
    loading,
    error,
    apiConfig: {
      baseUrl: env.API_BASE_URL,
      hasApiKey: !!env.API_KEY,
    },
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading agent details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="text-red-600 dark:text-red-400 mb-4">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <h1 className="text-2xl font-bold mb-2">Failed to Load Agent</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>
      </div>
    );
  }

  // Not found state
  if (!agent) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Agent Not Found</h1>
        <p className="text-muted-foreground mb-8">
          The infrastructure agent you're looking for doesn't exist.
        </p>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>
      </div>
    );
  }

  const handleLike = async () => {
    if (!agent || isLiking) return;

    // Check client-side if user can like this agent
    const likeCheck = canLike(agent.id);

    if (!likeCheck.allowed) {
      setLikeStatus("already-liked");
      logger.log(
        `Already liked this agent. Try again in ${likeCheck.hoursRemaining} hour(s)`,
      );
      // Reset status after showing message
      setTimeout(() => setLikeStatus("idle"), 5000);
      return;
    }

    setIsLiking(true);
    setLikeStatus("idle");

    try {
      const result = await likeAgent();
      if (result?.success) {
        // Record the like in localStorage
        recordLike(agent.id);

        setLikeStatus("success");
        logger.log("Agent liked successfully!");
        // Reset status after animation
        setTimeout(() => setLikeStatus("idle"), 2000);
      } else {
        setLikeStatus("error");
        logger.error("Failed to like agent:", result?.error);
        // Reset status after showing error
        setTimeout(() => setLikeStatus("idle"), 3000);
      }
    } catch (error) {
      setLikeStatus("error");
      logger.error("Failed to like agent:", error);
      // Reset status after showing error
      setTimeout(() => setLikeStatus("idle"), 3000);
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <Link
            to="/"
            className="inline-flex items-center text-base text-slate-600 dark:text-slate-400 hover:text-orange-500 dark:hover:text-orange-400 mb-8 transition-colors"
          >
            <ArrowLeft className="mr-3 h-5 w-5" />
            Back to Agents
          </Link>

          <div className="text-center">
            {/* Title */}
            <h1 className="text-5xl lg:text-6xl font-bold mb-8 text-slate-900 dark:text-white leading-tight">
              {agent.title}
            </h1>

            {/* Description */}
            <p className="text-2xl text-slate-700 dark:text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto">
              {agent.description}
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center space-x-8 mb-10 text-lg text-slate-600 dark:text-slate-400">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex items-center space-x-2 group transition-all duration-200 ${
                  isLiking
                    ? "cursor-not-allowed opacity-70"
                    : "hover:text-red-500 cursor-pointer"
                } ${likeStatus === "success" ? "text-red-500 scale-105" : ""}`}
              >
                <Heart
                  className={`h-6 w-6 text-red-500 transition-transform duration-200 ${
                    likeStatus === "success"
                      ? "fill-current animate-pulse scale-110"
                      : "group-hover:scale-110"
                  }`}
                />
                <span
                  className={`font-semibold transition-all duration-200 ${
                    likeStatus === "success" ? "scale-105" : ""
                  }`}
                >
                  {formatNumber(agent.stats.likes)} likes
                  {likeStatus === "success" && (
                    <span className="ml-1 animate-bounce">❤️</span>
                  )}
                </span>
              </button>
              <div className="flex items-center space-x-2">
                <Eye className="h-6 w-6 text-blue-500" />
                <span className="font-semibold">
                  {formatNumber(agent.stats.views)} views
                </span>
              </div>
            </div>

            {/* Competencies, Tags and License */}
            <div className="space-y-6 mb-10">
              {/* License - positioned first for prominence */}
              {((agent.license && agent.license !== "Unknown") ||
                (agent.metadata?.license &&
                  agent.metadata.license !== "Unknown")) && (
                <div className="flex justify-center">
                  <div
                    className="inline-flex items-center bg-green-100 dark:bg-green-900/30 px-4 py-2 border border-green-200 dark:border-green-700/50"
                    style={{ borderRadius: "10px" }}
                  >
                    <Scale className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                      {agent.license || agent.metadata?.license}
                    </span>
                  </div>
                </div>
              )}

              {/* Competencies */}
              {agent.competencies && agent.competencies.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                    Competencies
                  </h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {agent.competencies.map((competency) => (
                      <span
                        key={competency}
                        className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/30 px-4 py-2 text-base font-medium text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700/50"
                      >
                        {competency}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {agent.tags && agent.tags.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                    Tags
                  </h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {agent.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-900/30 px-4 py-2 text-base font-medium text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700/50"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              {agent.githubUrl && (
                <a
                  href={agent.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl bg-orange-500 hover:bg-orange-600 px-8 py-4 text-lg font-semibold text-white transition-colors"
                >
                  <Github className="mr-3 h-6 w-6" />
                  View on GitHub
                </a>
              )}
              {agent.websiteUrl && (
                <a
                  href={agent.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 px-8 py-4 text-lg font-semibold text-slate-900 dark:text-slate-100 transition-colors"
                >
                  <ExternalLink className="mr-3 h-6 w-6" />
                  Visit Website
                </a>
              )}
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`inline-flex items-center justify-center rounded-xl border-2 px-8 py-4 text-lg font-semibold transition-all duration-200 transform hover:scale-105 active:scale-95 group relative overflow-hidden ${
                  likeStatus === "success"
                    ? "border-green-400 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                    : likeStatus === "already-liked"
                      ? "border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
                      : likeStatus === "error"
                        ? "border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                        : "border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400"
                } ${isLiking ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
              >
                {isLiking ? (
                  <>
                    <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                    Liking...
                  </>
                ) : likeStatus === "success" ? (
                  <>
                    <Heart className="mr-3 h-6 w-6 fill-current animate-pulse" />
                    Liked! ❤️
                  </>
                ) : likeStatus === "already-liked" ? (
                  <>
                    <Heart className="mr-3 h-6 w-6 fill-current" />
                    Already Liked
                  </>
                ) : likeStatus === "error" ? (
                  <>
                    <AlertCircle className="mr-3 h-6 w-6" />
                    Try Again
                  </>
                ) : (
                  <>
                    <Heart className="mr-3 h-6 w-6 transition-transform duration-200 group-hover:scale-110" />
                    Like Agent
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="text-center">
          {agent.markdownContent ? (
            <>
              <MarkdownRenderer
                content={agent.markdownContent}
                className="text-left"
              />
            </>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-16 text-center">
              <p className="text-xl text-slate-600 dark:text-slate-400">
                No additional documentation provided for this agent.
              </p>
              <p className="text-lg text-slate-500 dark:text-slate-500 mt-4">
                Check the GitHub repository or website for more details.
              </p>
            </div>
          )}
        </div>

        {/* Additional Information */}
        <div className="mt-16 flex justify-center">
          {/* Statistics Card */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white text-center">
              Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-lg text-slate-600 dark:text-slate-400">
                  Likes
                </span>
                <span
                  className={`text-xl font-bold transition-all duration-300 ${
                    likeStatus === "success"
                      ? "text-red-600 dark:text-red-400 scale-110"
                      : "text-slate-900 dark:text-white"
                  }`}
                >
                  {formatNumber(agent.stats.likes)}
                  {likeStatus === "success" && (
                    <span className="ml-1 animate-bounce">❤️</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg text-slate-600 dark:text-slate-400">
                  Views
                </span>
                <span className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(agent.stats.views)}
                </span>
              </div>
              {/* GitHub Stars feature temporarily hidden */}
              {/* {((agent.githubStars != null && agent.githubStars > 0) || (agent.metadata?.github_stars != null && agent.metadata.github_stars > 0)) && (
                <div className="flex justify-between items-center">
                  <span className="text-lg text-slate-600 dark:text-slate-400">GitHub Stars</span>
                  <span className="text-xl font-bold text-slate-900 dark:text-white">{formatNumber(agent.githubStars || agent.metadata?.github_stars || 0)}</span>
                </div>
              )} */}
              {(agent.primaryLanguage &&
                agent.primaryLanguage !== "Unknown" &&
                agent.primaryLanguage !== "0") ||
                (agent.metadata?.language &&
                  agent.metadata.language !== "Unknown" &&
                  agent.metadata.language !== "0" && (
                    <div className="flex justify-between items-center">
                      <span className="text-lg text-slate-600 dark:text-slate-400">
                        Language
                      </span>
                      <span className="text-xl font-bold text-slate-900 dark:text-white">
                        {agent.primaryLanguage || agent.metadata?.language}
                      </span>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetail;
